/**
 * Agent planner profiles + process test (admin)
 * Route: /agent-planner
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { AiConfigModal } from '../components/aiconfig';
import {
  hubService,
  type AgentPlannerProcessResult,
  type AgentPlannerProfile,
} from '../services/hub.service';

type TabId = 'profiles' | 'test';

const DEFAULT_SYSTEM = 'chatbot';

function emptyProfileForm() {
  return {
    name: '',
    system: DEFAULT_SYSTEM,
    botId: 'default',
    enabled: true,
    systemPromptOverride: '',
    maxLoopIterations: '',
    toolRankTopK: '',
    enableToolRanking: '' as '' | 'default' | 'true' | 'false',
    preRetrieveRag: '' as '' | 'default' | 'true' | 'false',
    minConfidence: '',
    includeBuiltinTools: true,
    appendIntentCapabilities: 'default' as '' | 'default' | 'true' | 'false',
    appendToolCapabilities: 'default' as '' | 'default' | 'true' | 'false',
    appendServiceCapabilities: 'default' as '' | 'default' | 'true' | 'false',
    appendRagGuidance: 'default' as '' | 'default' | 'true' | 'false',
  };
}

type ProfileForm = ReturnType<typeof emptyProfileForm>;

function profileToForm(p: AgentPlannerProfile): ProfileForm {
  return {
    name: p.name,
    system: p.system,
    botId: p.botId,
    enabled: p.enabled,
    systemPromptOverride: p.systemPromptOverride ?? '',
    maxLoopIterations: p.maxLoopIterations != null ? String(p.maxLoopIterations) : '',
    toolRankTopK: p.toolRankTopK != null ? String(p.toolRankTopK) : '',
    enableToolRanking:
      p.enableToolRanking === null || p.enableToolRanking === undefined
        ? 'default'
        : p.enableToolRanking
          ? 'true'
          : 'false',
    preRetrieveRag:
      p.preRetrieveRag === null || p.preRetrieveRag === undefined
        ? 'default'
        : p.preRetrieveRag
          ? 'true'
          : 'false',
    minConfidence: p.minConfidence != null ? String(p.minConfidence) : '',
    includeBuiltinTools: p.includeBuiltinTools,
    appendIntentCapabilities:
      p.appendIntentCapabilities === null || p.appendIntentCapabilities === undefined
        ? 'default'
        : p.appendIntentCapabilities
          ? 'true'
          : 'false',
    appendToolCapabilities:
      p.appendToolCapabilities === null || p.appendToolCapabilities === undefined
        ? 'default'
        : p.appendToolCapabilities
          ? 'true'
          : 'false',
    appendServiceCapabilities:
      p.appendServiceCapabilities === null || p.appendServiceCapabilities === undefined
        ? 'default'
        : p.appendServiceCapabilities
          ? 'true'
          : 'false',
    appendRagGuidance:
      p.appendRagGuidance === null || p.appendRagGuidance === undefined
        ? 'default'
        : p.appendRagGuidance
          ? 'true'
          : 'false',
  };
}

function triBool(v: '' | 'default' | 'true' | 'false'): boolean | null | undefined {
  if (v === 'default' || v === '') return undefined;
  return v === 'true';
}

function optionalInt(s: string): number | undefined {
  const t = s.trim();
  if (!t) return undefined;
  const n = Number(t);
  return Number.isFinite(n) ? n : undefined;
}

function optionalFloat(s: string): number | undefined {
  const t = s.trim();
  if (!t) return undefined;
  const n = Number(t);
  return Number.isFinite(n) ? n : undefined;
}

export function AgentPlannerControl() {
  const { getToken } = useAuth();
  const [tab, setTab] = useState<TabId>('profiles');

  const [profilesLoading, setProfilesLoading] = useState(true);
  const [profilesError, setProfilesError] = useState<string | null>(null);
  const [profiles, setProfiles] = useState<AgentPlannerProfile[]>([]);

  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<ProfileForm>(emptyProfileForm);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const [testMessage, setTestMessage] = useState('What services do you offer?');
  const [testUserId, setTestUserId] = useState('admin-test-user');
  const [testSystem, setTestSystem] = useState(DEFAULT_SYSTEM);
  const [testBotId, setTestBotId] = useState('default');
  const [testIncludePlan, setTestIncludePlan] = useState(true);
  const [testMaxLoops, setTestMaxLoops] = useState('');
  const [testTopK, setTestTopK] = useState('');
  const [testMinConf, setTestMinConf] = useState('');
  const [testLoading, setTestLoading] = useState(false);
  const [testError, setTestError] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<AgentPlannerProcessResult | null>(null);

  const loadProfiles = useCallback(async () => {
    setProfilesLoading(true);
    setProfilesError(null);
    try {
      const token = getToken();
      const data = await hubService.listAgentPlannerProfiles(token);
      setProfiles(data);
    } catch (e) {
      setProfilesError(e instanceof Error ? e.message : 'Failed to load profiles');
    } finally {
      setProfilesLoading(false);
    }
  }, [getToken]);

  useEffect(() => {
    void loadProfiles();
  }, [loadProfiles]);

  const openCreate = () => {
    setModalMode('create');
    setEditingId(null);
    setForm(emptyProfileForm());
    setShowModal(true);
  };

  const openEdit = (p: AgentPlannerProfile) => {
    setModalMode('edit');
    setEditingId(p.id);
    setForm(profileToForm(p));
    setShowModal(true);
  };

  const submitModal = useCallback(async () => {
    if (!form.name.trim() || !form.system.trim() || !form.botId.trim()) {
      alert('Name, system, and bot ID are required.');
      return;
    }
    setSaving(true);
    try {
      const token = getToken();
      const ml = optionalInt(form.maxLoopIterations);
      const tk = optionalInt(form.toolRankTopK);
      const mc = optionalFloat(form.minConfidence);
      const et = triBool(form.enableToolRanking);
      const pr = triBool(form.preRetrieveRag);
      const ai = triBool(form.appendIntentCapabilities);
      const at = triBool(form.appendToolCapabilities);
      const as = triBool(form.appendServiceCapabilities);
      const ar = triBool(form.appendRagGuidance);

      if (modalMode === 'create') {
        await hubService.createAgentPlannerProfile(
          {
            name: form.name.trim(),
            system: form.system.trim(),
            botId: form.botId.trim(),
            enabled: form.enabled,
            systemPromptOverride: form.systemPromptOverride.trim() || null,
            maxLoopIterations: ml ?? null,
            toolRankTopK: tk ?? null,
            enableToolRanking: et ?? null,
            preRetrieveRag: pr ?? null,
            minConfidence: mc ?? null,
            includeBuiltinTools: form.includeBuiltinTools,
            appendIntentCapabilities: ai ?? null,
            appendToolCapabilities: at ?? null,
            appendServiceCapabilities: as ?? null,
            appendRagGuidance: ar ?? null,
          },
          token,
        );
      } else if (editingId) {
        await hubService.patchAgentPlannerProfile(
          editingId,
          {
            name: form.name.trim(),
            enabled: form.enabled,
            systemPromptOverride: form.systemPromptOverride.trim() || null,
            maxLoopIterations: ml ?? null,
            toolRankTopK: tk ?? null,
            enableToolRanking: et ?? null,
            preRetrieveRag: pr ?? null,
            minConfidence: mc ?? null,
            includeBuiltinTools: form.includeBuiltinTools,
            appendIntentCapabilities: ai ?? null,
            appendToolCapabilities: at ?? null,
            appendServiceCapabilities: as ?? null,
            appendRagGuidance: ar ?? null,
          },
          token,
        );
      }
      setShowModal(false);
      await loadProfiles();
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  }, [editingId, form, getToken, loadProfiles, modalMode]);

  const removeProfile = useCallback(
    async (id: string) => {
      if (!confirm('Delete this planner profile?')) return;
      setDeletingId(id);
      try {
        const token = getToken();
        await hubService.deleteAgentPlannerProfile(id, token);
        await loadProfiles();
      } catch (e) {
        alert(e instanceof Error ? e.message : 'Delete failed');
      } finally {
        setDeletingId(null);
      }
    },
    [getToken, loadProfiles],
  );

  const runTest = useCallback(async () => {
    if (!testMessage.trim() || !testUserId.trim()) {
      setTestError('Message and user ID are required.');
      return;
    }
    setTestLoading(true);
    setTestError(null);
    setTestResult(null);
    try {
      const token = getToken();
      const ml = optionalInt(testMaxLoops);
      const tk = optionalInt(testTopK);
      const mc = optionalFloat(testMinConf);
      const result = await hubService.agentPlannerProcess(
        {
          message: testMessage.trim(),
          userId: testUserId.trim(),
          system: testSystem.trim() || undefined,
          botId: testBotId.trim() || undefined,
          includePlan: testIncludePlan,
          maxLoopIterations: ml,
          toolRankTopK: tk,
          minConfidence: mc,
        },
        token,
      );
      setTestResult(result);
    } catch (e) {
      setTestError(e instanceof Error ? e.message : 'Process failed');
    } finally {
      setTestLoading(false);
    }
  }, [
    getToken,
    testBotId,
    testIncludePlan,
    testMaxLoops,
    testMessage,
    testMinConf,
    testSystem,
    testTopK,
    testUserId,
  ]);

  const tabs = useMemo(
    () =>
      [
        { id: 'profiles' as const, label: 'Profiles' },
        { id: 'test' as const, label: 'Run test' },
      ] as const,
    [],
  );

  return (
    <div className="h-full w-full overflow-y-auto bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Agent planner</h1>
        <p className="text-gray-600 mb-8">
          CRUD lives on{' '}
          <code className="text-xs bg-gray-100 px-1 rounded">GET/POST/PATCH/DELETE /api/agent-planner/profiles</code>.
          Runtime flow:{' '}
          <code className="text-xs bg-gray-100 px-1 rounded">POST /api/agent-planner/process</code> (separate
          controller). One profile per <span className="font-mono text-xs">system</span> +{' '}
          <span className="font-mono text-xs">botId</span> merges defaults into each run.
        </p>

        <div className="flex gap-2 mb-6 border-b border-gray-200">
          {tabs.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
                tab === t.id
                  ? 'bg-white border border-b-0 border-gray-200 -mb-px text-blue-600'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === 'profiles' && (
          <>
            <div className="mb-4 flex justify-end">
              <button
                type="button"
                onClick={openCreate}
                className="rounded bg-blue-600 px-3 py-1.5 text-sm text-white hover:bg-blue-700"
              >
                Add profile
              </button>
            </div>
            {profilesError && (
              <div className="mb-4 p-3 rounded-md bg-red-50 text-red-700 text-sm">{profilesError}</div>
            )}
            {profilesLoading ? (
              <div className="text-gray-500">Loading profiles…</div>
            ) : (
              <div className="bg-white rounded-lg border border-gray-200 overflow-hidden overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead>
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">System</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Bot</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Enabled</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Built-ins</th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {profiles.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="px-4 py-4 text-sm text-gray-500">
                          No profiles. Add one to tune planner defaults per bot; omit to use env defaults only.
                        </td>
                      </tr>
                    ) : (
                      profiles.map((p) => (
                        <tr key={p.id}>
                          <td className="px-4 py-2 text-sm text-gray-900">{p.name}</td>
                          <td className="px-4 py-2 text-sm font-mono text-xs text-gray-700">{p.system}</td>
                          <td className="px-4 py-2 text-sm font-mono text-xs text-gray-700">{p.botId}</td>
                          <td className="px-4 py-2 text-sm">{p.enabled ? 'yes' : 'no'}</td>
                          <td className="px-4 py-2 text-sm">{p.includeBuiltinTools ? 'yes' : 'no'}</td>
                          <td className="px-4 py-2 text-right space-x-2">
                            <button
                              type="button"
                              onClick={() => openEdit(p)}
                              className="text-sm text-blue-600 hover:text-blue-800"
                            >
                              Edit
                            </button>
                            <button
                              type="button"
                              onClick={() => void removeProfile(p.id)}
                              disabled={deletingId === p.id}
                              className="text-sm text-red-600 hover:text-red-800 disabled:opacity-50"
                            >
                              {deletingId === p.id ? '…' : 'Delete'}
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {tab === 'test' && (
          <section className="space-y-4">
            <div className="rounded-lg border border-gray-200 bg-white p-4 text-sm text-gray-700 space-y-2">
              <p className="font-medium text-gray-900">Test flow</p>
              <ol className="list-decimal list-inside space-y-1 text-gray-600">
                <li>
                  Calls <code className="text-xs bg-gray-100 px-1 rounded">POST /api/agent-planner/process</code> with
                  message + user id + scope.
                </li>
                <li>
                  Hub loads optional profile for <span className="font-mono text-xs">system</span> +{' '}
                  <span className="font-mono text-xs">botId</span>, then runs planner (pool{' '}
                  <span className="font-mono text-xs">executeText</span>) + tools loop.
                </li>
              </ol>
            </div>
            <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <label className="flex flex-col gap-1">
                  <span className="text-xs text-gray-500">System</span>
                  <input
                    value={testSystem}
                    onChange={(e) => setTestSystem(e.target.value)}
                    className="rounded border border-gray-300 px-2 py-1.5 text-sm font-mono"
                    disabled={testLoading}
                  />
                </label>
                <label className="flex flex-col gap-1">
                  <span className="text-xs text-gray-500">Bot ID</span>
                  <input
                    value={testBotId}
                    onChange={(e) => setTestBotId(e.target.value)}
                    className="rounded border border-gray-300 px-2 py-1.5 text-sm font-mono"
                    disabled={testLoading}
                  />
                </label>
                <label className="flex flex-col gap-1 sm:col-span-2">
                  <span className="text-xs text-gray-500">User ID (session key)</span>
                  <input
                    value={testUserId}
                    onChange={(e) => setTestUserId(e.target.value)}
                    className="rounded border border-gray-300 px-2 py-1.5 text-sm font-mono"
                    disabled={testLoading}
                  />
                </label>
              </div>
              <label className="flex flex-col gap-1">
                <span className="text-xs text-gray-500">Message</span>
                <textarea
                  value={testMessage}
                  onChange={(e) => setTestMessage(e.target.value)}
                  rows={4}
                  className="rounded border border-gray-300 px-2 py-1.5 text-sm"
                  disabled={testLoading}
                />
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <label className="flex flex-col gap-1">
                  <span className="text-xs text-gray-500">Max loop iterations (optional)</span>
                  <input
                    value={testMaxLoops}
                    onChange={(e) => setTestMaxLoops(e.target.value)}
                    placeholder="1–10"
                    className="rounded border border-gray-300 px-2 py-1.5 text-sm"
                    disabled={testLoading}
                  />
                </label>
                <label className="flex flex-col gap-1">
                  <span className="text-xs text-gray-500">Tool rank top-K (optional)</span>
                  <input
                    value={testTopK}
                    onChange={(e) => setTestTopK(e.target.value)}
                    placeholder="1–20"
                    className="rounded border border-gray-300 px-2 py-1.5 text-sm"
                    disabled={testLoading}
                  />
                </label>
                <label className="flex flex-col gap-1">
                  <span className="text-xs text-gray-500">Min confidence (optional)</span>
                  <input
                    value={testMinConf}
                    onChange={(e) => setTestMinConf(e.target.value)}
                    placeholder="0–1"
                    className="rounded border border-gray-300 px-2 py-1.5 text-sm"
                    disabled={testLoading}
                  />
                </label>
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={testIncludePlan}
                  onChange={(e) => setTestIncludePlan(e.target.checked)}
                  disabled={testLoading}
                />
                Include parsed plan in response (debug)
              </label>
              <button
                type="button"
                onClick={() => void runTest()}
                disabled={testLoading}
                className="rounded bg-blue-600 px-3 py-1.5 text-sm text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {testLoading ? 'Running…' : 'Run process'}
              </button>
              {testError && (
                <div className="p-3 rounded-md bg-red-50 text-red-700 text-sm whitespace-pre-wrap">{testError}</div>
              )}
              {testResult && (
                <div className="rounded-md border border-green-200 bg-green-50 p-3 text-sm space-y-2">
                  <div className="text-xs text-green-900 flex flex-wrap gap-x-4">
                    <span>
                      kind: <span className="font-mono">{testResult.kind}</span>
                    </span>
                  </div>
                  <p className="text-gray-900 whitespace-pre-wrap">{testResult.response}</p>
                  <pre className="text-xs bg-white/80 border border-green-100 rounded p-2 overflow-x-auto max-h-96 overflow-y-auto">
                    {JSON.stringify(testResult, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          </section>
        )}

        {showModal && (
          <AiConfigModal
            title={modalMode === 'create' ? 'Add planner profile' : 'Edit planner profile'}
            maxWidth="lg"
            onClose={() => {
              setShowModal(false);
              setEditingId(null);
            }}
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              <label className="flex flex-col gap-1 sm:col-span-2">
                <span className="text-xs text-gray-500">Name *</span>
                <input
                  value={form.name}
                  onChange={(e) => setForm((s) => ({ ...s, name: e.target.value }))}
                  className="rounded border border-gray-300 px-2 py-1.5"
                />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-xs text-gray-500">System *</span>
                <input
                  value={form.system}
                  onChange={(e) => setForm((s) => ({ ...s, system: e.target.value }))}
                  className="rounded border border-gray-300 px-2 py-1.5 font-mono text-xs"
                  disabled={modalMode === 'edit'}
                />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-xs text-gray-500">Bot ID *</span>
                <input
                  value={form.botId}
                  onChange={(e) => setForm((s) => ({ ...s, botId: e.target.value }))}
                  className="rounded border border-gray-300 px-2 py-1.5 font-mono text-xs"
                  disabled={modalMode === 'edit'}
                />
              </label>
              <p className="text-xs text-gray-500 sm:col-span-2">
                System and bot ID cannot be changed after create (unique scope).
              </p>
              <label className="flex flex-col gap-1 sm:col-span-2">
                <span className="text-xs text-gray-500">Planner system prompt override (optional)</span>
                <textarea
                  value={form.systemPromptOverride}
                  onChange={(e) => setForm((s) => ({ ...s, systemPromptOverride: e.target.value }))}
                  rows={4}
                  className="rounded border border-gray-300 px-2 py-1.5 font-mono text-xs"
                  placeholder="Leave empty to use hub default planner prompt"
                />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-xs text-gray-500">Max loop iterations</span>
                <input
                  value={form.maxLoopIterations}
                  onChange={(e) => setForm((s) => ({ ...s, maxLoopIterations: e.target.value }))}
                  placeholder="empty = env default"
                  className="rounded border border-gray-300 px-2 py-1.5"
                />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-xs text-gray-500">Tool rank top-K</span>
                <input
                  value={form.toolRankTopK}
                  onChange={(e) => setForm((s) => ({ ...s, toolRankTopK: e.target.value }))}
                  placeholder="empty = env default"
                  className="rounded border border-gray-300 px-2 py-1.5"
                />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-xs text-gray-500">Min confidence (0–1)</span>
                <input
                  value={form.minConfidence}
                  onChange={(e) => setForm((s) => ({ ...s, minConfidence: e.target.value }))}
                  placeholder="empty = env / request"
                  className="rounded border border-gray-300 px-2 py-1.5"
                />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-xs text-gray-500">Tool ranking</span>
                <select
                  value={form.enableToolRanking}
                  onChange={(e) =>
                    setForm((s) => ({ ...s, enableToolRanking: e.target.value as ProfileForm['enableToolRanking'] }))
                  }
                  className="rounded border border-gray-300 px-2 py-1.5"
                >
                  <option value="default">Default (on)</option>
                  <option value="true">On</option>
                  <option value="false">Off</option>
                </select>
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-xs text-gray-500">Pre-retrieve RAG heuristic</span>
                <select
                  value={form.preRetrieveRag}
                  onChange={(e) =>
                    setForm((s) => ({ ...s, preRetrieveRag: e.target.value as ProfileForm['preRetrieveRag'] }))
                  }
                  className="rounded border border-gray-300 px-2 py-1.5"
                >
                  <option value="default">Default (on)</option>
                  <option value="true">On</option>
                  <option value="false">Off</option>
                </select>
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-xs text-gray-500">Append intent/workflow capabilities</span>
                <select
                  value={form.appendIntentCapabilities}
                  onChange={(e) =>
                    setForm((s) => ({
                      ...s,
                      appendIntentCapabilities: e.target.value as ProfileForm['appendIntentCapabilities'],
                    }))
                  }
                  className="rounded border border-gray-300 px-2 py-1.5"
                >
                  <option value="default">Default (on)</option>
                  <option value="true">On</option>
                  <option value="false">Off</option>
                </select>
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-xs text-gray-500">Append tool capabilities</span>
                <select
                  value={form.appendToolCapabilities}
                  onChange={(e) =>
                    setForm((s) => ({
                      ...s,
                      appendToolCapabilities: e.target.value as ProfileForm['appendToolCapabilities'],
                    }))
                  }
                  className="rounded border border-gray-300 px-2 py-1.5"
                >
                  <option value="default">Default (on)</option>
                  <option value="true">On</option>
                  <option value="false">Off</option>
                </select>
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-xs text-gray-500">Append service/route capabilities</span>
                <select
                  value={form.appendServiceCapabilities}
                  onChange={(e) =>
                    setForm((s) => ({
                      ...s,
                      appendServiceCapabilities: e.target.value as ProfileForm['appendServiceCapabilities'],
                    }))
                  }
                  className="rounded border border-gray-300 px-2 py-1.5"
                >
                  <option value="default">Default (on)</option>
                  <option value="true">On</option>
                  <option value="false">Off</option>
                </select>
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-xs text-gray-500">Append RAG guidance</span>
                <select
                  value={form.appendRagGuidance}
                  onChange={(e) =>
                    setForm((s) => ({
                      ...s,
                      appendRagGuidance: e.target.value as ProfileForm['appendRagGuidance'],
                    }))
                  }
                  className="rounded border border-gray-300 px-2 py-1.5"
                >
                  <option value="default">Default (on)</option>
                  <option value="true">On</option>
                  <option value="false">Off</option>
                </select>
              </label>
              <div className="flex flex-wrap items-center gap-4 sm:col-span-2 pt-1">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={form.enabled}
                    onChange={(e) => setForm((s) => ({ ...s, enabled: e.target.checked }))}
                  />
                  Enabled (planner runs for this scope)
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={form.includeBuiltinTools}
                    onChange={(e) => setForm((s) => ({ ...s, includeBuiltinTools: e.target.checked }))}
                  />
                  Include built-in tool stubs
                </label>
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-4 border-t border-gray-100">
              <button
                type="button"
                className="rounded border border-gray-300 px-3 py-1.5 text-sm"
                onClick={() => {
                  setShowModal(false);
                  setEditingId(null);
                }}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                type="button"
                className="rounded bg-blue-600 px-3 py-1.5 text-sm text-white hover:bg-blue-700 disabled:opacity-50"
                onClick={() => void submitModal()}
                disabled={saving}
              >
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </AiConfigModal>
        )}
      </div>
    </div>
  );
}
